import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import { ThemeToggleButton } from './ThemeToggle';
import { getRoleDisplayName } from '../utils/roleDisplayNames';
import ProfileModal from './ProfileModal';
import { API_HOST, authService } from '../services/api';
import { useOrgFilter } from '../contexts/OrgFilterContext';

/**
 * Shared app Header (top bar).
 * Appears above every authenticated page. Shows:
 *   - PaperBridge brand wordmark (left)
 *   - Page title derived from current route (center-left)
 *   - User info, notifications, theme toggle, profile/logout (right)
 */

const ROUTE_TITLES: { pattern: RegExp; title: string }[] = [
  { pattern: /^\/records$/, title: 'Records' },
  { pattern: /^\/organization$/, title: 'Organization' },
  { pattern: /^\/platform/, title: 'Platform' },
  { pattern: /^\/timesheets\/report/, title: 'Timesheet Report' },
  { pattern: /^\/timesheets\/\d+/, title: 'Timesheet' },
  { pattern: /^\/timesheets/, title: 'Timesheet' },
  { pattern: /^\/dashboard/, title: 'Dashboard' },
  { pattern: /^\/documents/, title: 'Documents' },
  { pattern: /^\/admin/, title: 'Admin Dashboard' },
  { pattern: /^\/auditor/, title: 'Documents' },
  { pattern: /^\/tasks\/[^/]+/, title: 'Task Details' },
  { pattern: /^\/tasks/, title: 'Task Management' },
  { pattern: /^\/compliance/, title: 'Compliance Calendar' },
  { pattern: /^\/timesheets\/team/, title: 'Team Timesheets' },
  { pattern: /^\/timesheets\/report/, title: 'Timesheet Report' },
  { pattern: /^\/timesheets\/\d+/, title: 'Timesheet' },
  { pattern: /^\/timesheets/, title: 'My Timesheet' },
  { pattern: /^\/master-data/, title: 'Master Data' },
  { pattern: /^\/field-definitions/, title: 'Field Definitions' },
  { pattern: /^\/document-field-config/, title: 'Document Types' },
  { pattern: /^\/organizations-admin/, title: 'Organizations' },
  { pattern: /^\/organization/, title: 'Organizations' },
  { pattern: /^\/departments-admin/, title: 'Departments' },
  { pattern: /^\/property-locations/, title: 'Hard-Copy Locations' },
  { pattern: /^\/storage-config/, title: 'Storage Configuration' },
  { pattern: /^\/subscription/, title: 'Subscription & Plan' },
  { pattern: /^\/configurations/, title: 'Configurations' },
  { pattern: /^\/integrations\/zoho/, title: 'Zoho Books Integration' },
  { pattern: /^\/users/, title: 'Users' },
  { pattern: /^\/roles/, title: 'Roles & Permissions' },
  { pattern: /^\/sla-dashboard/, title: 'Team Performance' },
  { pattern: /^\/sla-config/, title: 'SLA Configuration' },
  { pattern: /^\/workflow-settings/, title: 'Approval Matrix' },
  { pattern: /^\/insurances-report/, title: 'Insurance Documents Report' },
  { pattern: /^\/licenses-report/, title: 'License Documents Report' },
  { pattern: /^\/agreements-report/, title: 'Agreement Documents Report' },
  { pattern: /^\/insurances/, title: 'Insurances' },
  { pattern: /^\/real-estate-report/, title: 'Real Estate Documents Report' },
  { pattern: /^\/real-estate/, title: 'Real Estate Documents' },
  { pattern: /^\/licenses/, title: 'Licenses & Renewals' },
  { pattern: /^\/agreements/, title: 'Agreements' },
  { pattern: /^\/records\/pending-approvals/, title: 'Acknowledgements' },
  { pattern: /^\/process/, title: 'Document' },
  // Pages that had no entry here showed the bare product name instead.
  { pattern: /^\/recycle-bin/, title: 'Recycle Bin' },
  { pattern: /^\/insights/, title: 'Insights' },
  { pattern: /^\/approvals/, title: 'Approvals' },
  { pattern: /^\/records/, title: 'Records' },
  { pattern: /^\/organization$/, title: 'Organization' },
  { pattern: /^\/automation/, title: 'Automation' },
  { pattern: /^\/platform/, title: 'Platform' },
  { pattern: /^\/workflow-tasks/, title: 'My Approvals' },
  { pattern: /^\/workflow-studio/, title: 'Workflow Studio' },
  { pattern: /^\/rule-studio/, title: 'Rule Studio' },
  { pattern: /^\/arbitrator/, title: 'Arbitrator' },
  { pattern: /^\/(external|vendor)-master/, title: 'External Master' },
  { pattern: /^\/tax-config/, title: 'Tax & Tolerances' },
  { pattern: /^\/connectors/, title: 'ERP Connectors' },
  { pattern: /^\/forensic-config/, title: 'Forensic Detection' },
  { pattern: /^\/whatsapp-senders/, title: 'WhatsApp Senders' },
  { pattern: /^\/recipe-stats/, title: 'Recipe Stats' },
  { pattern: /^\/search/, title: 'Search Documents' },
];

// Pages under the sidebar "Settings" group. The global org switcher is hidden
// on these (an org control, where needed, will live inside the page instead).
const SETTINGS_ROUTE_PREFIXES = [
  '/organization',
  '/automation',
  '/platform',
  '/users',
  '/recycle-bin',
  '/roles',
  '/organizations-admin',
  '/departments-admin',
  '/property-locations',
  '/field-definitions',
  '/document-field-config',
  '/workflow-settings',
  '/sla-config',
  '/storage-config',
  '/configurations',
  '/subscription',
  '/integrations',
];

interface HeaderProps {
  user?: any;
}

const Header: React.FC<HeaderProps> = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orgId, setOrgId, orgs, canSwitch } = useOrgFilter();
  const isSettingsRoute = SETTINGS_ROUTE_PREFIXES.some((p) => location.pathname.startsWith(p));
  const showOrgSwitcher = canSwitch && !isSettingsRoute;
  const [profileOpen, setProfileOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  // The `user` prop is captured at login, so its avatar_url goes stale the moment
  // the user uploads/removes a photo from the Profile modal — the top-right avatar
  // wouldn't update until a full reload. Track it locally: seed from the prop and
  // refresh on the 'pb:avatar-updated' event the modal dispatches.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatar_url ?? null);
  useEffect(() => { setAvatarUrl(user?.avatar_url ?? null); }, [user?.avatar_url]);
  useEffect(() => {
    const onAvatar = (e: Event) => setAvatarUrl((e as CustomEvent).detail?.avatar_url ?? null);
    window.addEventListener('pb:avatar-updated', onAvatar as EventListener);
    return () => window.removeEventListener('pb:avatar-updated', onAvatar as EventListener);
  }, []);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const getTitle = (): string => {
    for (const { pattern, title } of ROUTE_TITLES) {
      if (pattern.test(location.pathname)) return title;
    }
    return 'PaperBridge';
  };

  const handleLogout = async () => {
    // Revoke the server session/refresh cookie before clearing locally — a
    // localStorage-only logout left the session live (audit §5).
    await authService.logoutEverywhere();
    // Let app-wide providers (e.g. the org switcher) reset their state now.
    window.dispatchEvent(new Event('auth-changed'));
    navigate('/login');
  };

  const roleDisplay = user?.role
    ? getRoleDisplayName(user.role, user?.department?.department_type)
    : '';
  const orgName = user?.organization?.name || '';
  const displayName = user?.full_name || user?.username || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <header
      style={{
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        background: 'var(--color-background-primary, #FFFFFF)',
        borderBottom: '1px solid var(--color-border-light, var(--color-gray-200))',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
        fontFamily: "'Poppins', sans-serif",
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      {/* Phase 9 — PaperBridge wordmark removed from header (lives in the sidebar now). Page title on left. */}
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <h1
          style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: 600,
            color: 'var(--color-text-primary, var(--color-gray-900))',
            fontFamily: "'Poppins', sans-serif",
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            letterSpacing: '-0.01em',
          }}
        >
          {getTitle()}
        </h1>
      </div>

      {/* Right: org switcher, user info, notifications, theme, profile */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {showOrgSwitcher && (
          <select
            value={orgId === 'all' ? '' : String(orgId)}
            onChange={(e) => setOrgId(e.target.value ? Number(e.target.value) : 'all')}
            title="Filter by organization"
            style={{
              padding: '6px 10px',
              fontSize: '13px',
              fontFamily: "'Poppins', sans-serif",
              color: 'var(--color-text-primary, var(--color-gray-900))',
              background: 'var(--color-background-secondary, var(--color-gray-50))',
              border: '1px solid var(--color-border-light, var(--color-gray-200))',
              borderRadius: '20px',
              width: 'auto',
              maxWidth: '340px',
              cursor: 'pointer',
            }}
          >
            <option value="">All organizations</option>
            {orgs.map((o) => (
              <option key={o.organization_id} value={o.organization_id}>{o.name}</option>
            ))}
          </select>
        )}
        {user && (
          <div
            style={{
              textAlign: 'right',
              lineHeight: 1.2,
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '220px',
            }}
          >
            <span
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-text-primary, var(--color-gray-900))',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayName}
            </span>
            <span
              style={{
                fontSize: '11px',
                color: 'var(--color-text-secondary, var(--color-gray-500))',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {orgName}
              {orgName && roleDisplay ? ' · ' : ''}
              {roleDisplay}
            </span>
          </div>
        )}

        <NotificationBell variant="light" />
        <ThemeToggleButton />

        {/* Profile avatar with dropdown */}
        <div ref={profileRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setProfileOpen((v) => !v)}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: avatarUrl
                ? `url(${API_HOST}${avatarUrl}) center/cover`
                : 'linear-gradient(135deg, #1E3A8A 0%, #16A34A 100%)',
              color: '#FFFFFF',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: "'Poppins', sans-serif",
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Profile menu"
          >
            {!avatarUrl && (initials || 'U')}
          </button>
          {profileOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: '180px',
                background: 'var(--color-background-primary, #FFFFFF)',
                border: '1px solid var(--color-border-light, var(--color-gray-200))',
                borderRadius: '20px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                padding: '4px',
                zIndex: 100,
              }}
            >
              <div
                style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid var(--color-border-light, var(--color-gray-200))',
                  fontSize: '12px',
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  {displayName}
                </div>
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '11px', marginTop: '2px' }}>
                  {user?.email}
                </div>
              </div>
              <button
                onClick={() => { setProfileOpen(false); setShowProfileModal(true); }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontFamily: "'Poppins', sans-serif",
                  color: 'var(--color-primary-text)',
                  borderRadius: '4px',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#EFF6FF')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                My Profile
              </button>
              <button
                onClick={handleLogout}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 12px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontFamily: "'Poppins', sans-serif",
                  color: '#DC2626',
                  borderRadius: '4px',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#FEE2E2')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                Sign out
              </button>
            </div>
          )}
          {showProfileModal && <ProfileModal onClose={() => setShowProfileModal(false)} />}
        </div>
      </div>
    </header>
  );
};

export default Header;
